import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";

import { App } from "./App.js";
import { MotionProvider } from "./lib/motion.js";
import { ErrorBoundary } from "../../components/ErrorBoundary.js";
import { initAnalytics } from "./lib/analytics.js";

/*
 * Self-hosted type for the landing page, one CSS entry per weight actually
 * used. @fontsource ships the woff2 files inside the bundle, so the deployed
 * Worker serves them from its own asset binding: no request to a font host, no
 * font-shift caused by a blocked third party, and nothing to configure for a
 * host who opens the site somewhere with a slow connection.
 *
 * Latin subset only — the page has no non-Latin copy, and this keeps the built
 * asset list to the files a visitor can actually need.
 */
import "@fontsource/poppins/latin-600.css";
import "@fontsource/poppins/latin-700.css";
import "@fontsource/poppins/latin-800.css";
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/caveat/latin-600.css";
import "@fontsource/caveat/latin-700.css";

import "./styles.css";
/* The product side of the design language. Loaded after the base sheet so it
   can retheme the components styles.css documents without editing them. */
import "./app.css";
import "./landing.css";
/* Movement for the product pages; see the header of motion.css. */
import "./motion.css";

/**
 * Root.
 *
 * The router future flags are opted into here rather than left to warn.
 * `v7_startTransition` wraps navigation state updates in `startTransition`,
 * which is what keeps a slow route from blocking input. `v7_relativeSplatPath`
 * changes how a relative link resolves inside `path="*"` route, and we have
 * one — so the behaviour is a real question here rather than a generic warning,
 * and pinning it now means the upgrade does not move underneath us.
 *
 * <ClerkProvider> wraps everything, including the router: Clerk's own
 * components navigate on their own (a sign-in redirect lands back on the page
 * you were on), and it needs the router to be mounted to do that.
 *
 * SDK generation note: this project is on `@clerk/react` 6.x, which is the
 * current generation. Its conditional component is `<Show when="signed-in">`.
 *
 * Do not "upgrade" this to `@clerk/clerk-react` — that package is deprecated on
 * npm ("no longer supported, please use @clerk/react"). The rename ran the
 * other way: Core 3 folded `@clerk/clerk-react` INTO `@clerk/react` and merged
 * `<Protect>`/`<SignedIn>`/`<SignedOut>` into the single `<Show>` we use below.
 * Those three components were removed in Core 3, so importing them now gets you
 * nothing at all, with no error to explain why.
 */

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  // Fail loudly and immediately. A missing key otherwise surfaces much later as
  // an empty sign-in box with nothing in the console to explain it.
  throw new Error(
    "VITE_CLERK_PUBLISHABLE_KEY is missing. Add it to the project-root .env — see .env.",
  );
}

// ── PostHog analytics (free tier: 1M events/month) ──────────────────────────
// Keys live in the project-root .env and are gitignored. Create a project at
// app.posthog.com, copy the Project API key + host, then add them to .env:
//   VITE_POSTHOG_PROJECT_KEY=phc_…
//   VITE_POSTHOG_HOST=https://app.posthog.com
const posthogKey = import.meta.env.VITE_POSTHOG_PROJECT_KEY?.trim();
const posthogHost = import.meta.env.VITE_POSTHOG_HOST?.trim() ?? "https://app.posthog.com";
if (posthogKey) {
  initAnalytics(posthogKey, posthogHost);
} else {
  console.info("[eventgram] PostHog not configured — set VITE_POSTHOG_PROJECT_KEY in .env to enable analytics.");
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <ClerkProvider
        publishableKey={publishableKey}
        // Keep every Clerk navigation on this origin. Without these, the
        // "Sign up" link leaves for Clerk's Account Portal, and a signed-in
        // host with no profile is sent to a page this app does not control.
        signInUrl="/login"
        signUpUrl="/sign-up"
        afterSignOutUrl="/login"
        signInFallbackRedirectUrl="/dashboard"
        signUpFallbackRedirectUrl="/dashboard"
      >
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <MotionProvider>
            <App />
          </MotionProvider>
        </BrowserRouter>
      </ClerkProvider>
    </ErrorBoundary>
  </StrictMode>,
);